import { conferenceLabel } from '../lib/text.js'
import { Link } from 'react-router-dom'
import { formatVenueTime } from '../lib/datetime.js'
import { icsUrl, googleCalendarUrl, linkedInShareUrl, twitterShareUrl } from '../lib/api.js'
import SubstackShareButton from './SubstackShareButton.jsx'

function dayLine(iso) {
  if (!iso) return ''
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(iso))
}

export default function EventMapPopup({ event }) {
  const main = (event.speakers || []).find((s) => s.isMain)
  const co = (event.speakers || []).filter((s) => !s.isMain)

  return (
    <div className="min-w-[220px] max-w-[280px] text-slate-900">
      <div className="text-base font-semibold leading-snug">{event.title}</div>
      {conferenceLabel(event) ? (
        <div className="mt-0.5 text-xs text-slate-500">{conferenceLabel(event)}</div>
      ) : null}

      <div className="mt-2 text-sm text-slate-700">{dayLine(event.startsAtUtc)}</div>
      {event.tzId ? (
        <div className="text-xs text-slate-500">
          Venue local: {formatVenueTime(event.startsAtUtc, event.tzId)} ({event.tzId})
        </div>
      ) : null}

      {main ? (
        <div className="mt-2 text-sm">
          <span className="font-semibold">{main.displayName || `User #${main.userId}`}</span>
          {co.length ? (
            <span className="text-slate-500"> with {co.map((s) => s.displayName || `User #${s.userId}`).join(', ')}</span>
          ) : null}
        </div>
      ) : null}

      {event.tags?.length ? (
        <ul className="mt-2 flex flex-wrap gap-1">
          {event.tags.slice(0, 4).map((t) => (
            <li
              key={t.slug}
              className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-700"
            >
              {t.name || t.slug}
            </li>
          ))}
        </ul>
      ) : null}

      <div className="mt-3 flex flex-wrap gap-2 text-xs">
        <Link
          to={`/events/${event.id}`}
          className="rounded-full px-2 py-1 font-medium text-brand-700 hover:bg-brand-50"
        >
          View details →
        </Link>
        <a
          href={googleCalendarUrl(event)}
          target="_blank"
          rel="noopener noreferrer"
          /* #354 exception: this renders inside a Leaflet popup, whose own
             stylesheet sets the base font size. A 44px pill would overflow the
             popup, so the map keeps a scaled-down spelling. */
          className="rounded-full bg-brand-600 px-3 py-1 font-medium text-white hover:bg-brand-700"
        >
          Add to Calendar
        </a>
        <a
          href={icsUrl(event.id)}
          download
          className="rounded-full px-2 py-1 font-medium text-brand-700 hover:bg-brand-50"
        >
          .ics
        </a>
        {event.registrationUrl ? (
          <a
            href={event.registrationUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-full px-2 py-1 font-medium text-slate-700 hover:bg-slate-100"
          >
            Register ↗
          </a>
        ) : null}
        <a
          href={linkedInShareUrl(event)}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Share on LinkedIn"
          title="Share on LinkedIn"
          className="inline-flex h-7 w-7 items-center justify-center rounded-full text-slate-700 hover:bg-slate-100"
        >
          <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
          </svg>
        </a>
        <a
          href={twitterShareUrl(event)}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Share on X (Twitter)"
          title="Share on X (Twitter)"
          className="inline-flex h-7 w-7 items-center justify-center rounded-full text-slate-700 hover:bg-slate-100"
        >
          <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
          </svg>
        </a>
        <SubstackShareButton
          event={event}
          className="inline-flex h-7 w-7 items-center justify-center rounded-full text-slate-700 hover:bg-slate-100"
          iconSize="h-3.5 w-3.5"
        />
      </div>
    </div>
  )
}
